import { useEffect, useState, useCallback } from 'react';

export type ViewMode = 'player' | 'coach';

const STORAGE_KEY = 'sportara-view-mode';

function readStored(): ViewMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'player' || v === 'coach' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Tracks which side of the app the user is currently viewing
 * (player experience vs. coach dashboard). Persists in localStorage
 * and syncs across components via a storage event.
 */
export function useViewMode(defaultMode: ViewMode = 'player') {
  const [mode, setModeState] = useState<ViewMode>(() => readStored() ?? defaultMode);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const next = readStored();
        if (next) setModeState(next);
      }
    };
    window.addEventListener('storage', handler);
    const internal = () => {
      const next = readStored();
      if (next) setModeState(next);
    };
    window.addEventListener('sportara-view-mode-change', internal);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('sportara-view-mode-change', internal);
    };
  }, []);

  const setMode = useCallback((next: ViewMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setModeState(next);
    window.dispatchEvent(new Event('sportara-view-mode-change'));
  }, []);

  return { mode, setMode };
}

export function setStoredViewMode(mode: ViewMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event('sportara-view-mode-change'));
}
